import { Controller } from "@hotwired/stimulus"

// A task's key facts. In the full view they wrap, so every fact shows. On a
// card (scroll: true) they keep to one row that scrolls sideways: the edge
// with more to see fades, and a "+N ›" button steps the row along — back to
// the start once it reaches the end.
//
// Hovering or focusing a fact shows its card: what it is, its value, Copy.
// Cards are popovers in the top layer, placed under the fact, because the
// task's head sits in boxes that clip their overflow. A card waits a moment
// before closing so the pointer can reach its buttons.
export default class extends Controller {
  static targets = ["item", "track", "more", "moreCount", "moreIcon"]
  static values = { scroll: Boolean }

  connect() {
    this.onKey = this.onKey.bind(this)
    this.fade = this.fade.bind(this)
    document.addEventListener("keydown", this.onKey)
    if (this.scrollValue && this.hasTrackTarget) {
      this.trackTarget.addEventListener("scroll", this.fade, { passive: true })
      this.resizeObserver = new ResizeObserver(this.fade)
      this.resizeObserver.observe(this.trackTarget)
      this.fade()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
    if (this.hasTrackTarget) this.trackTarget.removeEventListener("scroll", this.fade)
    this.resizeObserver?.disconnect()
    clearTimeout(this.hideTimer)
    this.hideCard()
  }

  // Fade whichever edge has more to see, and count what waits to the right.
  fade() {
    const track = this.trackTarget
    const more = track.scrollWidth - track.clientWidth > 1
    const atStart = track.scrollLeft <= 1
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
    if (!more) delete track.dataset.fade
    else track.dataset.fade = atStart ? "end" : atEnd ? "start" : "both"

    if (!this.hasMoreTarget) return
    this.moreTarget.hidden = !more
    if (!more) return
    const edge = track.getBoundingClientRect().right
    const waiting = this.itemTargets.filter((item) => item.getBoundingClientRect().right > edge + 1).length
    this.moreCountTarget.textContent = atEnd ? "" : `+${waiting}`
    this.moreIconTarget.className = `ti ${atEnd ? "ti-chevron-left" : "ti-chevron-right"}`
    this.moreTarget.setAttribute("aria-label", atEnd ? "Back to the first facts" : `Show ${waiting} more facts`)
  }

  advance() {
    const track = this.trackTarget
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    if (atEnd) track.scrollTo({ left: 0, behavior })
    else track.scrollBy({ left: Math.round(track.clientWidth * 0.75), behavior })
  }

  show(event) {
    const item = event.currentTarget
    const card = item.querySelector(":scope > .task-context__card")
    if (!card || !card.showPopover) return

    clearTimeout(this.hideTimer)
    if (this.card && this.card !== card) this.hideCard()
    this.card = card
    if (!card.matches(":popover-open")) card.showPopover()
    this.place(card, item.getBoundingClientRect())
  }

  keep() {
    clearTimeout(this.hideTimer)
  }

  hide() {
    clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => this.hideCard(), 160)
  }

  hideCard() {
    if (this.card?.matches(":popover-open")) this.card.hidePopover()
    this.card = null
  }

  onKey(event) {
    if (event.key === "Escape") this.hideCard()
  }

  // Under the anchor, its left edge on the anchor's, kept on screen; above it
  // when there is no room below.
  place(popover, anchor) {
    const margin = 8
    const box = popover.getBoundingClientRect()
    const left = Math.min(anchor.left, window.innerWidth - box.width - margin)
    let top = anchor.bottom + 6
    if (top + box.height > window.innerHeight - margin) top = anchor.top - box.height - 6
    popover.style.left = `${Math.max(margin, left)}px`
    popover.style.top = `${Math.max(margin, top)}px`
  }
}
